import { Alert, Box, CircularProgress } from "@mui/material";
import { type TreeItemProps } from "@mui/x-tree-view";
import { useCallback, useEffect, useMemo, useReducer, type FC } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import {
  ArchiveFolderModal,
  type ArchiveFolderData,
} from "../../modules/assets/archive-folder-modal";
import { AssetPreviewModal } from "../../modules/assets/asset-preview-modal";
import { ContentLibraryAssets } from "../../modules/assets/components/content-library-assets";
import { ContentLibraryFilter } from "../../modules/assets/components/content-library-filter";
import { ContentLibraryHeader } from "../../modules/assets/components/content-library-header";
import { ContentLibrarySidebar } from "../../modules/assets/components/content-library-sidebar";
import { CustomTreeItem } from "../../modules/assets/components/custom-tree-item";
import { FolderBreadcrumb } from "../../modules/assets/components/folder-breadcrumb";
import { ContentTypeChips } from "../../modules/assets/content-type-chips";
import { contentTypes as allContentTypes } from "../../modules/assets/content-types";
import { CreateFolderModal, type CreateFolderData } from "../../modules/assets/create-folder-modal";
import { EmptyState } from "../../modules/assets/empty-state";
import { useAssets } from "../../modules/assets/hooks/use-assets";
import { useFolders } from "../../modules/assets/hooks/use-folders";
import {
  contentLibraryReducer,
  filterAssets,
  initialState,
} from "../../modules/assets/reducer/reducer";
import type { Asset } from "../../modules/assets/server/digital-asset-service";
import { TagChips } from "../../modules/assets/tag-chips";
import { UploadNewFileModal } from "../../modules/assets/upload-new-file-modal";
import {
  constructBreadcrumbs,
  folderNameFromId,
  folderParentMap,
  ROOT_ID,
  transformFoldersToTreeItems,
} from "../../modules/assets/utils";
import { useAccount } from "../../modules/generic/hooks/use-account";
import { Page } from "../../modules/layout/components/page";
import { PageContent } from "../../modules/layout/components/page-content";
import { SearchField } from "../../modules/search/search-field";
import { useFlag } from "../../server/optimizely";
import { useDialog } from "../../utils/hooks/use-dialog";
import type { UUID } from "../../utils/uuid";

// TODO: This will need changing once the tags are fetched from the backend
function extractAvailableTags(assets: Asset[]): string[] {
  return Array.from(
    new Set(
      assets.filter((asset) => asset.tags && asset.tags.length > 0).flatMap((asset) => asset.tags!),
    ),
  ).sort();
}

export const ContentLibraryPageNewInner: FC = () => {
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(contentLibraryReducer, initialState);
  const [searchParams, setSearchParams] = useSearchParams();
  const [video_uploads] = useFlag("video_uploads");

  const previewModal = useDialog();
  const uploadModal = useDialog();
  const createFolderModal = useDialog<CreateFolderData>();
  const archiveFolderModal = useDialog<ArchiveFolderData>();
  const { handleOpen: openCreateFolderModal } = createFolderModal;
  const { handleOpen: openArchiveFolderModal } = archiveFolderModal;

  const { data, loading, error, reload } = useAssets();
  const {
    data: foldersData,
    loading: foldersLoading,
    error: foldersError,
    reload: reloadFolders,
  } = useFolders();
  const account = useAccount();

  useEffect(() => {
    const formats = searchParams.has("format") ? searchParams.getAll("format") : [];
    const tags = searchParams.has("tags") ? searchParams.getAll("tags") : [];
    const previewAssetId = searchParams.get("preview");
    const selectedFolderId = searchParams.get("folder") ?? ROOT_ID;

    dispatch({
      type: "SYNC_FROM_URL",
      payload: { contentTypes: formats, tags, previewAssetId, selectedFolderId },
    });
  }, [searchParams]);

  useEffect(() => {
    if (state.previewAssetId && !previewModal.open) {
      previewModal.handleOpen();
    } else if (!state.previewAssetId && previewModal.open) {
      previewModal.handleClose();
    }
  }, [state.previewAssetId, previewModal]);

  const selectedAsset = useMemo(() => {
    if (!state.previewAssetId || loading || !data) return null;
    return data.find((x) => x.id === state.previewAssetId) ?? null;
  }, [state.previewAssetId, data, loading]);

  const treeItems = useMemo(() => {
    if (!foldersData || foldersError || foldersLoading) return [];
    return transformFoldersToTreeItems(foldersData ?? []);
  }, [foldersData, foldersError, foldersLoading]);

  const folderMap = useMemo(() => {
    return folderParentMap(foldersData);
  }, [foldersData]);

  useEffect(() => {
    if (!state.selectedTreeItemId || treeItems.length === 0) return;

    const findPath = (
      items: typeof treeItems,
      targetId: string,
      path: string[],
    ): string[] | null => {
      for (const item of items) {
        const currentPath = [...path, item.id];
        if (item.id === targetId) return currentPath;
        if (item.children?.length) {
          const result = findPath(item.children, targetId, currentPath);
          if (result) return result;
        }
      }
      return null;
    };

    const path = findPath(treeItems, state.selectedTreeItemId, []);
    if (!path) return;

    const parentIds = path.slice(0, -1);
    const nextExpanded = Array.from(new Set([...state.expandedItems, ...parentIds]));
    if (nextExpanded.length !== state.expandedItems.length) {
      dispatch({ type: "SET_EXPANDED_ITEMS", payload: nextExpanded });
    }
  }, [state.selectedTreeItemId, state.expandedItems, treeItems]);

  // TODO: This will need changing once the tags are fetched from the backend
  const availableTags = useMemo(() => {
    if (!data) return [];
    return extractAvailableTags(data);
  }, [data]);

  // Compute filtered assets based on current state
  const filteredData = useMemo(() => {
    if (!data) return null;
    return filterAssets(
      data,
      {
        selectedTreeItemId: state.selectedTreeItemId,
        appliedContentTypes: state.appliedContentTypes,
        appliedTags: state.appliedTags,
        searchText: state.searchText,
      },
      treeItems,
    );
  }, [
    data,
    state.selectedTreeItemId,
    state.appliedContentTypes,
    state.appliedTags,
    state.searchText,
    treeItems,
  ]);

  const filteredFolders = useMemo(() => {
    if (!foldersData) return [];

    const folderId = searchParams.get("folder");

    return foldersData
      .filter((folder) => folder.parent_folder_id === (folderId === ROOT_ID ? null : folderId))
      .filter((folder) => folder.name.toLowerCase().includes(state.searchText.toLowerCase()))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [foldersData, searchParams, state.searchText]);

  const breadcrumbs = useMemo(() => {
    return constructBreadcrumbs(treeItems, state.selectedTreeItemId);
  }, [treeItems, state.selectedTreeItemId]);

  const handleFolderSelect = useCallback(
    (folderId: string): void => {
      dispatch({ type: "SELECT_FOLDER", payload: folderId });

      const params = new URLSearchParams(searchParams);
      if (folderId === ROOT_ID) {
        params.delete("folder");
      } else {
        params.set("folder", folderId);
      }
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const handleSelectAsset = useCallback(
    (asset: Asset) => {
      dispatch({ type: "SET_PREVIEW_ASSET_ID", payload: asset.id });
      const params = new URLSearchParams(searchParams);
      params.set("preview", asset.id);
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const handleClosePreviewModal = useCallback(() => {
    dispatch({ type: "SET_PREVIEW_ASSET_ID", payload: null });
    const params = new URLSearchParams(searchParams);
    params.delete("preview");
    setSearchParams(params);
  }, [searchParams, setSearchParams]);

  const handleArchived = useCallback(() => {
    handleClosePreviewModal();
    reload();
  }, [handleClosePreviewModal, reload]);

  const handleTreeItemClick = useCallback(
    (_event: React.MouseEvent, itemId: string): void => {
      handleFolderSelect(itemId);
    },
    [handleFolderSelect],
  );

  const handleRemoveContentTypeFilter = useCallback(
    (contentType: string) => {
      dispatch({ type: "REMOVE_CONTENT_TYPE_FILTER", payload: contentType });

      const params = new URLSearchParams(searchParams);
      const newValues = params.getAll("format").filter((c) => c !== contentType);
      params.delete("format");
      newValues.forEach((val) => params.append("format", val));
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const handleRemoveTagFilter = useCallback(
    (tag: string) => {
      dispatch({ type: "REMOVE_TAG_FILTER", payload: tag });

      const params = new URLSearchParams(searchParams);
      const newValues = params.getAll("tags").filter((tagValue) => tagValue !== tag);
      params.delete("tags");
      newValues.forEach((val) => params.append("tags", val));
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const handleApplyFilter = useCallback(() => {
    dispatch({ type: "APPLY_FILTERS" });

    const params = new URLSearchParams();
    state.selectedContentTypes.forEach((c) => params.append("format", c));
    state.selectedTags.forEach((tag) => params.append("tags", tag));
    if (state.selectedTreeItemId && state.selectedTreeItemId !== ROOT_ID) {
      params.set("folder", state.selectedTreeItemId);
    }
    setSearchParams(params);
  }, [state.selectedContentTypes, state.selectedTags, state.selectedTreeItemId, setSearchParams]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: "SET_SEARCH_TEXT", payload: e.target.value });
  }, []);

  const handleSidebarToggle = useCallback((open: boolean) => {
    dispatch({ type: "SET_SIDEBAR_OPEN", payload: open });
  }, []);

  const handleExpandedItemsChange = useCallback(
    (_event: React.SyntheticEvent | null, itemIds: string[]) => {
      dispatch({ type: "SET_EXPANDED_ITEMS", payload: itemIds });
    },
    [],
  );
  const isFolderInArchivedPath = useCallback(
    (selectedId: string, archivedId: UUID): boolean => {
      if (!selectedId || selectedId === ROOT_ID) return false;
      let current: string | null = selectedId;
      while (current && current !== ROOT_ID) {
        if (current === archivedId) return true;
        current = folderMap.get(current as UUID) ?? null;
      }
      return false;
    },
    [folderMap],
  );

  const handleSetSelectedContentTypes = useCallback(
    (contentTypes: string[] | ((prev: string[]) => string[])) => {
      if (typeof contentTypes === "function") {
        // For functional updates, we'd need current state - but since ContentLibraryFilter
        // only passes arrays directly, we can handle this case if needed
        return;
      }
      dispatch({ type: "SET_SELECTED_CONTENT_TYPES", payload: contentTypes });
    },
    [],
  );

  const handleSetSelectedTags = useCallback((tags: string[] | ((prev: string[]) => string[])) => {
    if (typeof tags === "function") {
      return;
    }
    dispatch({ type: "SET_SELECTED_TAGS", payload: tags });
  }, []);

  const handleCreateSubFolder = useCallback(
    (parentFolderId: UUID | null) => {
      openCreateFolderModal({ parentFolderId });
    },
    [openCreateFolderModal],
  );

  const handleArchive = useCallback(
    (folderId: UUID | null) => {
      if (!folderId || !foldersData) {
        return;
      }
      openArchiveFolderModal({
        folderId,
        name: folderNameFromId(folderId, foldersData) ?? "",
      });
    },
    [foldersData, openArchiveFolderModal],
  );

  const CustomTreeItemWithReload = useCallback(
    (props: TreeItemProps) => (
      <CustomTreeItem
        {...props}
        reloadFolders={reloadFolders}
        onCreateSubFolder={handleCreateSubFolder}
        onArchive={handleArchive}
      />
    ),
    [handleCreateSubFolder, handleArchive, reloadFolders],
  );

  const selectedParentFolderId =
    state.selectedTreeItemId && state.selectedTreeItemId !== ROOT_ID
      ? (state.selectedTreeItemId as UUID)
      : null;

  return (
    <>
      <Page
        title={t("Content Library | ChangeEngine")}
        sx={{ height: "100%", display: "flex", flexDirection: "column" }}
      >
        <ContentLibraryHeader
          onUpload={() => uploadModal.handleOpen({ parentFolderId: state.selectedTreeItemId })}
        />
        <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
          <ContentLibrarySidebar
            loading={foldersLoading}
            sidebarOpen={state.sidebarOpen}
            treeItems={treeItems}
            selectedTreeItemId={state.selectedTreeItemId}
            expandedItems={state.expandedItems}
            onToggle={handleSidebarToggle}
            onItemClick={handleTreeItemClick}
            onExpandedItemsChange={handleExpandedItemsChange}
            onCreateSubFolder={() =>
              createFolderModal.handleOpen({ parentFolderId: selectedParentFolderId })
            }
            itemSlot={CustomTreeItemWithReload}
          />
          <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
            <PageContent maxWidth="xl">
              {loading && <CircularProgress />}
              {error && (
                <Alert severity="error">
                  {t("Sorry, there was a problem retrieving your content. Please try again later")}
                </Alert>
              )}
              {filteredData && (
                <ContentLibraryAssets
                  filteredData={filteredData}
                  filteredFolders={filteredFolders}
                  state={state}
                  slots={{
                    breadcrumb: (
                      <FolderBreadcrumb
                        breadcrumbs={breadcrumbs}
                        onFolderSelect={handleFolderSelect}
                      />
                    ),
                    searchField: (
                      <SearchField
                        label="Search"
                        defaultValue={state.searchText}
                        onChange={handleSearchChange}
                        fullWidth
                      />
                    ),
                    filter: (
                      <ContentLibraryFilter
                        handleApplyFilter={handleApplyFilter}
                        selectedContentTypes={state.selectedContentTypes}
                        setSelectedContentTypes={handleSetSelectedContentTypes}
                        contentTypes={allContentTypes}
                        selectedTags={state.selectedTags}
                        setSelectedTags={handleSetSelectedTags}
                        availableTags={availableTags}
                      />
                    ),
                    contentTypeChips: (
                      <ContentTypeChips
                        onDelete={handleRemoveContentTypeFilter}
                        contentTypes={state.appliedContentTypes}
                      />
                    ),
                    tagChips: (
                      <TagChips onDelete={handleRemoveTagFilter} tags={state.appliedTags} />
                    ),
                  }}
                  designer={account.data?.designer}
                  onSelectAsset={handleSelectAsset}
                  onCreateFolder={() =>
                    createFolderModal.handleOpen({ parentFolderId: selectedParentFolderId })
                  }
                  onArchiveFolder={() => {
                    if (
                      !foldersData ||
                      !state.selectedTreeItemId ||
                      state.selectedTreeItemId === ROOT_ID
                    ) {
                      return;
                    }
                    archiveFolderModal.handleOpen({
                      name: folderNameFromId(state.selectedTreeItemId as UUID, foldersData) ?? "",
                      folderId: state.selectedTreeItemId as UUID,
                    });
                  }}
                />
              )}
              {data && data.length === 0 && <EmptyState />}
            </PageContent>
          </Box>
        </Box>
      </Page>
      <AssetPreviewModal
        asset={selectedAsset}
        open={previewModal.open}
        onClose={handleClosePreviewModal}
        onChange={reload}
        onArchived={handleArchived}
        onViewFileClick={(asset) => {
          handleSelectAsset(asset);
        }}
        onFolderChange={reload}
        folders_feature_flag={true}
      />
      <UploadNewFileModal
        folders_feature_flag={true}
        open={uploadModal.open}
        onOpen={uploadModal.handleOpen}
        onClose={() => {
          uploadModal.handleClose();
          reloadFolders();
        }}
        onCancel={() => {
          uploadModal.handleClose();
          reloadFolders();
        }}
        onChange={(folderId) => {
          reload();
          handleFolderSelect(folderId ?? ROOT_ID);
        }}
        onViewFileClick={(asset) => {
          handleSelectAsset(asset);
        }}
        videoUploads={video_uploads}
        folderId={
          state.selectedTreeItemId && state.selectedTreeItemId !== ROOT_ID
            ? (state.selectedTreeItemId as UUID)
            : null
        }
      />
      <CreateFolderModal
        open={createFolderModal.open}
        onClose={createFolderModal.handleClose}
        reloadFolders={reloadFolders}
        parentFolderId={createFolderModal.data?.parentFolderId ?? null}
      />
      {archiveFolderModal.data && (
        <ArchiveFolderModal
          open={archiveFolderModal.open}
          onClose={() => {
            archiveFolderModal.handleClose();
            if (
              archiveFolderModal.data?.folderId &&
              state.selectedTreeItemId &&
              isFolderInArchivedPath(state.selectedTreeItemId, archiveFolderModal.data.folderId)
            ) {
              const params = new URLSearchParams(searchParams);
              params.delete("folder");
              setSearchParams(params);
            }
          }}
          reloadFolders={reloadFolders}
          name={archiveFolderModal.data.name}
          folderId={archiveFolderModal.data.folderId}
        />
      )}
    </>
  );
};
